import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Facebook, Instagram, Send, Loader2, CheckCircle2, XCircle,
  History, Sparkles, ExternalLink, Clock, Calendar, Save, PenLine,
  AlertTriangle, RotateCcw,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import ManualPostDialog from "./ManualPostDialog";

interface MetaLogRow {
  id: string;
  post_date: string;
  created_at: string;
  details: any;
  post_type?: string;
}

interface MetaSettings {
  meta_enabled: boolean;
  meta_fb_enabled: boolean;
  meta_ig_enabled: boolean;
  meta_post_days: number[];
  meta_send_hour: number;
  meta_send_minute: number;
  timezone_offset_minutes: number;
}

const DAYS = [
  { idx: 1, label: "Mon" },
  { idx: 2, label: "Tue" },
  { idx: 3, label: "Wed" },
  { idx: 4, label: "Thu" },
  { idx: 5, label: "Fri" },
  { idx: 6, label: "Sat" },
  { idx: 0, label: "Sun" },
];

function nextRunCountdown(s: MetaSettings): string {
  if (!s.meta_enabled) return "disabled";
  if (!s.meta_post_days || s.meta_post_days.length === 0) return "no days selected";
  const now = new Date();
  const tzMs = s.timezone_offset_minutes * 60 * 1000;
  const localNow = new Date(now.getTime() + tzMs);
  const target = new Date(localNow);
  target.setUTCHours(s.meta_send_hour, s.meta_send_minute, 0, 0);
  if (target <= localNow) target.setUTCDate(target.getUTCDate() + 1);
  let guard = 0;
  while (!s.meta_post_days.includes(target.getUTCDay()) && guard < 14) {
    target.setUTCDate(target.getUTCDate() + 1);
    guard++;
  }
  if (guard >= 14) return "never";
  const diff = target.getTime() - localNow.getTime();
  const hrs = Math.floor(diff / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  if (hrs >= 24) return `in ${Math.floor(hrs / 24)}d ${hrs % 24}h`;
  return `in ${hrs}h ${mins}m`;
}

const MetaSocialCard = ({ sessionToken }: { sessionToken: string | null }) => {
  const { toast } = useToast();
  const [sending, setSending] = useState(false);
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [logs, setLogs] = useState<MetaLogRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [manualOpen, setManualOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tick, setTick] = useState(0);
  const [s, setS] = useState<MetaSettings>({
    meta_enabled: true,
    meta_fb_enabled: true,
    meta_ig_enabled: true,
    meta_post_days: [1, 2, 3, 4, 5],
    meta_send_hour: 7,
    meta_send_minute: 0,
    timezone_offset_minutes: 180,
  });

  const countdown = useMemo(() => nextRunCountdown(s), [s, tick]);

  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 30_000);
    return () => clearInterval(t);
  }, []);

  const loadSettings = async () => {
    const { data: r } = await supabase.functions.invoke("admin-channel", { body: { action: "get_settings", sessionToken } });
    const data = (r as any)?.settings;
    if (data) {
      setS({
        meta_enabled: data.meta_enabled ?? true,
        meta_fb_enabled: data.meta_fb_enabled ?? true,
        meta_ig_enabled: data.meta_ig_enabled ?? true,
        meta_post_days: Array.isArray(data.meta_post_days) ? data.meta_post_days : [1, 2, 3, 4, 5],
        meta_send_hour: data.meta_send_hour ?? 7,
        meta_send_minute: data.meta_send_minute ?? 0,
        timezone_offset_minutes: data.timezone_offset_minutes ?? 180,
      });
    }
  };

  const loadLogs = async () => {
    const { data: r } = await supabase.functions.invoke("admin-channel", { body: { action: "list_logs", sessionToken, scope: "meta", limit: 7 } });
    setLogs((((r as any)?.logs) ?? []) as MetaLogRow[]);
    setLoading(false);
  };

  useEffect(() => {
    loadSettings();
    loadLogs();
    const poll = setInterval(() => loadLogs(), 15_000);
    return () => clearInterval(poll);
  }, []);

  const saveSettings = async () => {
    setSaving(true);
    const { data, error } = await supabase.functions.invoke("admin-channel", {
      body: {
        action: "save_settings",
        sessionToken,
        patch: {
          meta_enabled: s.meta_enabled,
          meta_fb_enabled: s.meta_fb_enabled,
          meta_ig_enabled: s.meta_ig_enabled,
          meta_post_days: s.meta_post_days,
          meta_send_hour: s.meta_send_hour,
          meta_send_minute: s.meta_send_minute,
        },
      },
    });
    const err = error?.message || (data as any)?.error;
    if (err) {
      toast({ title: "Save failed", description: err, variant: "destructive" });
    } else {
      toast({ title: "✅ Schedule saved" });
    }
    setSaving(false);
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const { data, error } = await supabase.functions.invoke("daily-channel-post", {
        body: { force: true, type: "meta_only" },
      });
      if (error) throw error;
      const { data: latestRes } = await supabase.functions.invoke("admin-channel", { body: { action: "list_logs", sessionToken, scope: "meta_latest", limit: 1 } });
      const latestLog = (latestRes as any)?.logs?.[0];
      const m = (latestLog as any)?.details?.meta;
      const fbOk = !!m?.fb?.id;
      const igOk = !!m?.ig?.id;
      const fbErr = m?.fb?.error?.message as string | undefined;
      const igErr = (m?.ig?.error?.message ?? m?.ig?.container?.error?.message) as string | undefined;

      if (fbOk || igOk) {
        toast({
          title: "Posted to Facebook + Instagram",
          description: `Facebook: ${fbOk ? "✓" : "✗"} · Instagram: ${igOk ? "✓" : "✗"}`,
        });
      } else {
        toast({
          title: "Meta posting failed",
          description:
            fbErr || igErr
              ? `${fbErr ?? igErr}. Check META_FB_PAGE_ACCESS_TOKEN scopes (pages_manage_posts, instagram_content_publish).`
              : "Both FB and IG failed. See recent posts below.",
          variant: "destructive",
        });
      }
      await loadLogs();
    } catch (e: any) {
      toast({ title: "Cross-post failed", description: e.message ?? "Unknown error", variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  // Extract a human-readable error from a meta result for FB and IG
  const extractMetaErrors = (m: any): { fbErr?: string; igErr?: string; igCode?: number; isPerm: boolean } => {
    const fbErr = m?.fb?.error?.message as string | undefined;
    const igErrMsg = (typeof m?.ig?.error === 'string' ? m?.ig?.error : m?.ig?.error?.message)
      ?? m?.ig?.container?.error?.message;
    const igCode = m?.ig?.container?.error?.code ?? m?.ig?.error?.code;
    const isPerm = igCode === 10 || /permission|content_publish|OAuthException/i.test(igErrMsg ?? "");
    return { fbErr, igErr: igErrMsg, igCode, isPerm };
  };

  const retryIg = async (logId: string) => {
    setRetryingId(logId);
    try {
      const { data, error } = await supabase.functions.invoke("daily-channel-post", {
        body: { type: "retry_ig", logId },
      });
      if (error) throw error;
      if ((data as any)?.ok) {
        toast({ title: "✅ Instagram published", description: `Media id: ${(data as any).ig?.id}` });
      } else {
        const reason = (data as any)?.reason || (data as any)?.ig?.error?.message || (data as any)?.ig?.container?.error?.message || "Retry failed";
        toast({ title: "Retry failed", description: reason, variant: "destructive" });
      }
      await loadLogs();
    } catch (e: any) {
      toast({ title: "Retry failed", description: e.message ?? "Unknown error", variant: "destructive" });
    } finally {
      setRetryingId(null);
    }
  };

  const latest = logs[0];
  const meta = latest?.details?.meta;
  const fbOk = !!meta?.fb?.id;
  const igOk = !!meta?.ig?.id;
  const skipped = meta?.skipped as string | undefined;
  const latestErrors = meta ? extractMetaErrors(meta) : null;
  const showLatestErrorBanner = latest && !skipped && (latestErrors?.fbErr || latestErrors?.igErr);

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-3 bg-gradient-to-r from-blue-500/10 via-pink-500/5 to-purple-500/10 border-b">
        <CardTitle className="flex items-center gap-2 text-base">
          <div className="flex -space-x-1">
            <div className="w-7 h-7 rounded-md bg-blue-500/15 text-blue-600 flex items-center justify-center border border-blue-500/30">
              <Facebook className="w-4 h-4" />
            </div>
            <div className="w-7 h-7 rounded-md bg-pink-500/15 text-pink-600 flex items-center justify-center border border-pink-500/30">
              <Instagram className="w-4 h-4" />
            </div>
          </div>
          <span className="flex-1">Facebook & Instagram Daily Post</span>
          <Badge variant="outline" className="text-[10px] gap-1">
            <Sparkles className="w-3 h-3" /> Auto
          </Badge>
        </CardTitle>
      </CardHeader>

      <CardContent className="p-4 space-y-4">
        <p className="text-xs text-muted-foreground leading-relaxed">
          A fresh AI-generated daily quote image is published to your Facebook Page and
          Instagram Business account on its own schedule (independent from Telegram).
        </p>

        {/* Schedule */}
        <div className="rounded-lg border bg-muted/30 p-3 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium flex items-center gap-1.5">
                <Clock className="w-4 h-4" /> Auto schedule
              </div>
              <div className="text-[11px] text-muted-foreground">Next: {countdown}</div>
            </div>
            <Switch
              checked={s.meta_enabled}
              onCheckedChange={(v) => setS({ ...s, meta_enabled: v })}
              aria-label="Enable Meta auto post"
            />
          </div>

          {/* Per-channel toggles — pause FB or IG independently */}
          <div className={`grid grid-cols-2 gap-2 ${s.meta_enabled ? "" : "opacity-50 pointer-events-none"}`}>
            <label className="flex items-center justify-between gap-2 p-2 rounded border bg-background">
              <span className="text-[11px] font-medium flex items-center gap-1.5">
                <Facebook className="w-3.5 h-3.5 text-blue-600" /> Facebook
              </span>
              <Switch
                checked={s.meta_fb_enabled}
                onCheckedChange={(v) => setS({ ...s, meta_fb_enabled: v })}
              />
            </label>
            <label className="flex items-center justify-between gap-2 p-2 rounded border bg-background">
              <span className="text-[11px] font-medium flex items-center gap-1.5">
                <Instagram className="w-3.5 h-3.5 text-pink-600" /> Instagram
              </span>
              <Switch
                checked={s.meta_ig_enabled}
                onCheckedChange={(v) => setS({ ...s, meta_ig_enabled: v })}
              />
            </label>
          </div>

          <div className={s.meta_enabled ? "" : "opacity-50 pointer-events-none"}>
            <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Time of day</Label>
            <div className="flex items-center gap-2 mt-1">
              <Input
                type="number" min={0} max={23}
                value={s.meta_send_hour}
                onChange={(e) => setS({ ...s, meta_send_hour: Math.max(0, Math.min(23, parseInt(e.target.value) || 0)) })}
                className="w-16 text-center font-bold"
              />
              <span className="font-bold text-muted-foreground">:</span>
              <Input
                type="number" min={0} max={59}
                value={s.meta_send_minute}
                onChange={(e) => setS({ ...s, meta_send_minute: Math.max(0, Math.min(59, parseInt(e.target.value) || 0)) })}
                className="w-16 text-center font-bold"
              />
              <span className="text-[11px] text-muted-foreground">channel local time</span>
            </div>

            <div className="mt-3">
              <Label className="text-[11px] uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                <Calendar className="w-3 h-3" /> Post days
              </Label>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {DAYS.map((d) => {
                  const active = s.meta_post_days.includes(d.idx);
                  return (
                    <Button
                      key={d.idx}
                      type="button"
                      size="sm"
                      variant={active ? "default" : "outline"}
                      onClick={() => {
                        const cur = new Set(s.meta_post_days);
                        if (active) cur.delete(d.idx); else cur.add(d.idx);
                        setS({ ...s, meta_post_days: Array.from(cur).sort((a, b) => a - b) });
                      }}
                      className="text-xs h-7 px-2.5 min-w-[42px]"
                    >
                      {d.label}
                    </Button>
                  );
                })}
              </div>
              <div className="flex flex-wrap gap-1.5 pt-2">
                <Button size="sm" variant="ghost" className="text-[11px] h-6"
                  onClick={() => setS({ ...s, meta_post_days: [1, 2, 3, 4, 5] })}>Weekdays</Button>
                <Button size="sm" variant="ghost" className="text-[11px] h-6"
                  onClick={() => setS({ ...s, meta_post_days: [0, 1, 2, 3, 4, 5, 6] })}>Every day</Button>
                <Button size="sm" variant="ghost" className="text-[11px] h-6"
                  onClick={() => setS({ ...s, meta_post_days: [0, 6] })}>Weekends only</Button>
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-1">
            <Button size="sm" onClick={saveSettings} disabled={saving}>
              {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
              Save schedule
            </Button>
          </div>
        </div>

        {/* Latest error banner — surfaces FB/IG failures with retry hint */}
        {showLatestErrorBanner && (
          <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-3 space-y-2">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-destructive">
                  Last post had errors ({latest.post_date})
                </div>
                {latestErrors?.fbErr && (
                  <div className="text-[11px] mt-1 break-words">
                    <span className="font-medium">Facebook:</span> {latestErrors.fbErr}
                  </div>
                )}
                {latestErrors?.igErr && (
                  <div className="text-[11px] mt-1 break-words">
                    <span className="font-medium">Instagram:</span> {latestErrors.igErr}
                  </div>
                )}
                {latestErrors?.isPerm && (
                  <div className="text-[11px] mt-2 p-2 rounded bg-amber-500/10 border border-amber-500/30 leading-relaxed">
                    🔑 <strong>Likely cause:</strong> the Page Access Token is missing the
                    {" "}<code className="px-1 rounded bg-muted">instagram_content_publish</code> permission, or the IG account isn't a Business/Creator account linked to your Facebook Page.
                    <div className="mt-1">
                      <strong>Fix:</strong> grant the permission in Meta Developer Console, generate a new long-lived token, update the
                      {" "}<code className="px-1 rounded bg-muted">META_FB_PAGE_ACCESS_TOKEN</code> secret, then press <strong>Retry IG</strong> below.
                    </div>
                  </div>
                )}
              </div>
            </div>
            {!igOk && !skipped && (
              <Button
                size="sm"
                variant="outline"
                className="w-full"
                disabled={retryingId === latest.id}
                onClick={() => retryIg(latest.id)}
              >
                {retryingId === latest.id
                  ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Retrying Instagram…</>
                  : <><RotateCcw className="w-3 h-3 mr-1" /> Retry Instagram publish</>}
              </Button>
            )}
          </div>
        )}

        {/* Connection status */}
        <div className="grid grid-cols-2 gap-2">
          <div className="p-3 rounded-lg border bg-muted/30">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[11px] font-medium flex items-center gap-1.5">
                <Facebook className="w-3.5 h-3.5 text-blue-600" /> Facebook Page
              </span>
              {fbOk
                ? <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
                : skipped
                  ? <XCircle className="w-3.5 h-3.5 text-amber-500" />
                  : <span className="text-[10px] text-muted-foreground">—</span>}
            </div>
            <div className="text-[10px] text-muted-foreground font-mono truncate">
              {fbOk ? `post: ${meta.fb.id}` : skipped ? "not configured" : "no posts yet"}
            </div>
          </div>

          <div className="p-3 rounded-lg border bg-muted/30">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[11px] font-medium flex items-center gap-1.5">
                <Instagram className="w-3.5 h-3.5 text-pink-600" /> Instagram Business
              </span>
              {igOk
                ? <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
                : skipped
                  ? <XCircle className="w-3.5 h-3.5 text-amber-500" />
                  : <span className="text-[10px] text-muted-foreground">—</span>}
            </div>
            <div className="text-[10px] text-muted-foreground font-mono truncate">
              {igOk ? `media: ${meta.ig.id}` : skipped ? "not configured" : "no posts yet"}
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="grid grid-cols-2 gap-2">
          <Button
            variant="default"
            disabled={sending}
            onClick={() => setManualOpen(true)}
          >
            <PenLine className="w-4 h-4 mr-2" /> Custom post
          </Button>
          <Button
            variant="outline"
            disabled={sending}
            onClick={sendNow}
          >
            {sending
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Posting…</>
              : <><Send className="w-4 h-4 mr-2" /> Auto quote now</>}
          </Button>
        </div>

        {/* Recent posts */}
        <div className="pt-2 border-t">
          <div className="flex items-center gap-1.5 mb-2 text-xs font-medium text-muted-foreground">
            <History className="w-3.5 h-3.5" /> Recent posts
          </div>
          {loading ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : logs.length === 0 ? (
            <p className="text-[11px] text-muted-foreground py-3 text-center">No posts yet.</p>
          ) : (
            <div className="space-y-1.5 max-h-56 overflow-y-auto">
              {logs.map((l) => {
                const m = l.details?.meta;
                const lFbOk = !!m?.fb?.id;
                const lIgOk = !!m?.ig?.id;
                const lSkipped = m?.skipped;
                const isManual = l.post_type === "manual_meta";
                return (
                  <div key={l.id} className="flex items-center gap-2 p-2 rounded border bg-background text-[11px]">
                    <span className="text-muted-foreground font-mono shrink-0">{l.post_date}</span>
                    {isManual && <Badge variant="secondary" className="text-[9px] h-4 px-1">manual</Badge>}
                    <div className="flex items-center gap-1 ml-auto">
                      <span className={`px-1.5 py-0.5 rounded gap-1 inline-flex items-center ${lFbOk ? "bg-green-500/10 text-green-600" : "bg-muted text-muted-foreground"}`}>
                        <Facebook className="w-3 h-3" />
                        {lFbOk ? "ok" : lSkipped ? "skip" : "—"}
                      </span>
                      <span className={`px-1.5 py-0.5 rounded gap-1 inline-flex items-center ${lIgOk ? "bg-green-500/10 text-green-600" : "bg-muted text-muted-foreground"}`}>
                        <Instagram className="w-3 h-3" />
                        {lIgOk ? "ok" : lSkipped ? "skip" : "—"}
                      </span>
                      {!lIgOk && !lSkipped && (m?.imagePath || m?.imageUrl) && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-5 px-1.5 text-[10px]"
                          disabled={retryingId === l.id}
                          onClick={() => retryIg(l.id)}
                          title="Retry Instagram publish"
                        >
                          {retryingId === l.id
                            ? <Loader2 className="w-3 h-3 animate-spin" />
                            : <RotateCcw className="w-3 h-3" />}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <p className="text-[10px] text-muted-foreground leading-relaxed border-t pt-3">
          ℹ️ Meta tokens are stored as secrets. To rotate the Page Access Token, update{" "}
          <code className="px-1 rounded bg-muted">META_FB_PAGE_ACCESS_TOKEN</code> in workspace secrets.
          Posting requires a long-lived token with{" "}
          <code className="px-1 rounded bg-muted">pages_manage_posts</code> and{" "}
          <code className="px-1 rounded bg-muted">instagram_content_publish</code> scopes.
          {" "}
          <a
            href="https://developers.facebook.com/tools/explorer/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary inline-flex items-center gap-0.5 hover:underline"
          >
            Graph API Explorer <ExternalLink className="w-2.5 h-2.5" />
          </a>
        </p>
      </CardContent>

      <ManualPostDialog
        open={manualOpen}
        onOpenChange={setManualOpen}
        type="manual_meta"
        title="Custom Facebook + Instagram post"
        description="Write your caption. An image is required for Instagram; Facebook works text-only."
        onPosted={loadLogs}
      />
    </Card>
  );
};

export default MetaSocialCard;
